/* eslint-disable @typescript-eslint/no-unused-vars */
import { call, put, select, delay, takeLatest } from 'redux-saga/effects'
import { notify } from 'src/components/ui/notify/Notify'
import Toast from 'react-hot-toast'
import { createSagas } from 'src/helpers/redux'
import { IResponse } from 'src/services/storage/project/http'
import * as schemaService from 'src/services/storage/project/scheme'
import * as iconifyService from 'src/services/iconify'
import { realtimeService } from 'src/services/realtime'
import * as sessionService from 'src/services/session'
import store from 'src/store'
import * as account from 'src/store/modules/account'
import * as state from './state'
import { actions, Actions, PREFIX } from './actions'
import { setters } from './index'
import { selectors } from './selectors'

const SCHEMAS_PER_PAGE = 10
// Бэк не отдаёт больше 100 схем за запрос
const SCHEMAS_PER_PAGE_MAX = 100

const sagas = createSagas<Actions>(actions, {
  tost: function* ({ payload }) {
    const id = notify(payload)
    if (payload.id) {
      yield put(setters.setToastId({ key: payload.id, id }))
    }
  },
  toastDismiss: function* ({ payload }) {
    const toasts: Record<string, string> = yield select(selectors.toasts)
    const id = toasts[payload.id]
    if (id) {
      Toast.dismiss(id)
      yield put(setters.setToastId({ key: payload.id, id: undefined }))
    }
  },
  navigate: {
    effect: 'every',
    saga: function* ({ payload }) {
      yield put(setters.setLocation(payload))
    },
  },

  fieldsGroups: function* ({ payload }) {
    yield put(setters.setFieldsGroups(payload))
  },
  getSingleSchema: {
    effect: 'every',
    saga: function* ({ payload }) {
      const singleSchema: IResponse<schemaService.ISchemaInfo> = yield call(
        schemaService.getSingleSchema,
        payload
      )

      yield put(
        setters.setSingleSchema({
          data: singleSchema.data,
          isAdmin: payload.isAdmin,
        })
      )
    },
  },
  getAdminSchemas: function* ({ payload }) {
    const adminSchemas: IResponse<schemaService.IGetSchemasData> = yield call(
      schemaService.getSchemas,
      payload
    )

    yield put(setters.setSchemas({ data: adminSchemas }))
  },
  getSchemas: function* ({ payload }) {
    const cachedSchemas: state.IStateData['schemas']['items'] = yield select(
      selectors.schemas
    )
    const perPage = Math.min(
      Math.max(payload.perPage || SCHEMAS_PER_PAGE, cachedSchemas.length),
      SCHEMAS_PER_PAGE_MAX
    )

    const schemas: IResponse<schemaService.IGetSchemasData> = yield call(
      schemaService.getSchemas,
      { showInMenu: true, perPage }
    )
    yield put(setters.setSchemas({ data: schemas, showInMenu: true }))
  },
  getSchemasByType: function* ({ payload }) {
    const schemas: IResponse<schemaService.IGetSchemasData> = yield call(
      schemaService.getSchemas,
      { perPage: payload.perPage, type: payload.type }
    )
    yield put(setters.setSchemesByType({ data: schemas }))
  },
  loadMoreAdminSchemas: function* ({ payload }) {
    const schemas: IResponse<schemaService.IGetSchemasData> = yield call(
      schemaService.getSchemas,
      { cursor: payload.cursor, perPage: payload.perPage }
    )

    yield put(setters.mergeSchemas({ data: schemas }))
  },

  loadMoreSchemas: function* ({ payload }) {
    const schemas: IResponse<schemaService.IGetSchemasData> = yield call(
      schemaService.getSchemas,
      { showInMenu: true, cursor: payload.cursor, perPage: payload.perPage }
    )

    yield put(setters.mergeSchemas({ data: schemas, showInMenu: true }))
  },

  postSchemas: function* ({ payload }) {
    const schema: IResponse<schemaService.IPostSchemasData> = yield call(
      schemaService.postSchemas,
      payload
    )

    yield put(setters.setSchemasAppend({ ...schema.data, children: [] }))

    const group = {
      schemeName: schema.data.identifier,
      isPredefined: true,
      showEmptyFields: true,
      showInMain: true,
      identifier: `${schema.data.identifier}_basic`,
      title: `${schema.data.title} basic`,
    }
    yield call(schemaService.postFieldsGroups, group)
  },
  putSchemas: function* ({ payload }) {
    const schemas: IResponse<schemaService.IPutSchemasData> = yield call(
      schemaService.putSchemas,
      payload
    )

    yield put(setters.setSchemasUpdate(schemas.data))
  },
  deleteSchemas: function* ({ payload }) {
    const schemas: IResponse<schemaService.IDeleteSchemasData> = yield call(
      schemaService.deleteSchemas,
      payload
    )
    yield put(setters.setSchemasDelete({ identifier: payload.identifier }))
  },
  logging: {
    effect: (pattern, saga) => takeLatest('*', saga),
    controlled: true,
    saga: function* (data) {
      if (data.type.includes(PREFIX) && !data.type.includes('cacheStorage')) {
        yield put(actions.cacheStorage.pending())
      }
    },
  },
  cacheStorage: {
    effect: 'latest',
    controlled: true,
    saga: function* (data) {
      if (data.type.includes(PREFIX)) {
        yield delay(1000)
        try {
          const stateData: state.IStateData = yield select(selectors.data)
          localStorage.setItem(PREFIX, JSON.stringify(stateData))
        } catch (e) {
          console.debug(e)
        }
      }
    },
  },
  realtimeConnection: function* (data) {
    realtimeService.connection()

    const session = sessionService.getSession()

    realtimeService.only<any>(
      {
        key: `account:${session.userId}:update`,
        event: 'user_update',
        channel: 'auth:users',
      },
      () => {
        store.dispatch(
          account.actions.getRefreshToken({ isUpdatePermissions: true })
        )
        store.dispatch(account.actions.getProfile())
      }
    )

    realtimeService.only<any>(
      {
        key: `account:update`,
        event: 'group_update',
        channel: 'auth:groups',
      },
      () => {
        store.dispatch(
          account.actions.getRefreshToken({ isUpdatePermissions: true })
        )
        store.dispatch(account.actions.getProfile())
      }
    )

    realtimeService.only<any>(
      {
        key: `account:store`,
        event: 'group_store',
        channel: 'auth:groups',
      },
      () => {
        store.dispatch(
          account.actions.getRefreshToken({ isUpdatePermissions: true })
        )
        store.dispatch(account.actions.getProfile())
      }
    )
  },

  getIconSearch: {
    effect: 'latest',
    pending: function* ({ payload }) {
      yield delay(3000)

      const search: IResponse<iconifyService.IGetIconSearchData> = yield call(
        iconifyService.getIconSearch,
        payload
      )
      yield put(
        setters.setIconSearch({
          params: payload,
          result: search.data,
        })
      )
    },
    clear: function* ({ payload }) {
      yield put(
        setters.setIconSearch({
          params: {
            query: '',
            start: 0,
            limit: 20,
          },
          result: {
            icons: [],
          },
        })
      )
    },
  },
  getIconSearchMore: {
    effect: 'leading',
    saga: function* () {
      yield delay(1000)

      const iconsSearch: state.IStateData['icons']['search'] = yield select(
        selectors.iconsSearch
      )

      const params = {
        ...iconsSearch.params,
        start:
          (iconsSearch.params.start || 0) + (iconsSearch.params.limit || 20),
      }
      const search: IResponse<iconifyService.IGetIconSearchData> = yield call(
        iconifyService.getIconSearch,
        params
      )
      yield put(setters.setIconSearchMore({ result: search.data, params }))
    },
  },
  smartListsCountUp: function* ({ payload }) {
    yield put(setters.setSmartListsCount(payload))
  },

  stageFieldsCountUp: function* ({ payload }) {
    yield put(setters.setStageFieldCount(payload))
  },
  // getIconCollection: function* ({ payload }) {
  //   const collection: IResponse<iconifyService.IGetIconCollectionData> = yield call(
  //     iconifyService.getIconSearch,
  //     payload
  //   )
  //   // yield put(setters.setIconCollection(collection.data))
  // },
})

export default sagas
